import { useSupportedCountries } from '../../util/platformAccess';
import React, { useState, useEffect } from 'react';
import { Modal, Button, Select, MenuItem, FormControl, InputLabel, ListItemButton, ListItemText, ListItemIcon, Typography, Grid, Box, List } from '@mui/material';
import { FaChevronDown } from 'react-icons/fa';
import { useQuery } from '@apollo/client';
import Flag from 'react-world-flags';
import { useTranslation } from 'react-i18next'; 
import { GET_ALL_COUNTRIES } from '../../graphql/queries/countryStateCity';

const RegionSelector = ({ onSelect }) => {
    const [open, setOpen] = useState(false);
    const [language, setLanguage] = useState('');
    const [country, setCountry] = useState('');
    const [countryCode, setCountryCode] = useState('');
    const [countryId, setCountryId] = useState('');
    const { countries: supported } = useSupportedCountries();
    const { data, loading, error } = useQuery(GET_ALL_COUNTRIES);

    useEffect(() => {
        const defaults = {
            selectedLanguage: 'English',
            selectedCountry: 'United States',
            selectedCountryCode: 'US',
            currentCountryId: 228
        };

        const savedValues = Object.keys(defaults).reduce((acc, key) => {
            const savedValue = localStorage.getItem(key);
            if (savedValue) {
                acc[key] = savedValue;
            } else {
                localStorage.setItem(key, defaults[key]);
                acc[key] = defaults[key];
            }
            return acc;
        }, {});

        setLanguage(savedValues.selectedLanguage);
        setCountry(savedValues.selectedCountry);
        setCountryCode(savedValues.selectedCountryCode);
        setCountryId(savedValues.currentCountryId);
    }, []);

    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const { t, i18n } = useTranslation();

    const handleLanguageChange = (event) => {
        setLanguage(event.target.value);
        localStorage.setItem('selectedLanguage', event.target.value);
        i18n.changeLanguage(event.target.value);
    };

    const handleCountrySelect = (selectedCountry, selectedCountryCode, selectedCountryId) => {
        setCountry(selectedCountry);
        setCountryCode(selectedCountryCode);
        setCountryId(selectedCountryId);
        localStorage.setItem('selectedCountry', selectedCountry);
        localStorage.setItem('selectedCountryCode', selectedCountryCode);
        localStorage.setItem('currentCountryId', selectedCountryId);
        if (typeof onSelect === 'function') {
            onSelect({ language, country: selectedCountry, countryCode: selectedCountryCode, countryId: selectedCountryId });
        }
        // hard reload the page
        window.location.reload();
        handleClose();
    };

    if (loading) return <p>Loading...</p>;
    if (error) return <p>Error :</p>;

    const countries = (data?.getAllCountries || []).filter(c => supported.some(s => s.code === c.code.toUpperCase()));

    // Group countries by region.name
    const groupedByRegion = countries.reduce((acc, country) => {
        const regionName = country.region.name;
        if (!acc[regionName]) {
            acc[regionName] = [];
        }
        acc[regionName].push(country);
        return acc;
    }, {});

    return (
        <>
            <Button onClick={handleOpen} variant="text" color="inherit">
                {countryCode && <Flag code={countryCode} height="16" style={{ marginRight: '0.1em' }} />}
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {country && countryCode}
                    <FaChevronDown
                        style={{
                            marginLeft: '2px',
                            color: 'inherit',
                        }}
                    />
                </Box>
            </Button>
            <Modal open={open} onClose={handleClose}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    bgcolor: 'background.paper',
                    color: 'text.primary',
                    border: 1,
                    borderColor: 'divider',
                    boxShadow: 24,
                    colorScheme: theme => theme.palette.mode,
                    padding: '20px',
                    borderRadius: '8px',
                    width: 'min(400px, 92vw)',
                    boxSizing: 'border-box',
                    maxHeight: '80vh',
                    overflowY: 'auto',
                }}>

                    {Object.keys(groupedByRegion).map((regionName) => (
                        <div key={regionName}>
                            <Typography variant="h6" style={{ marginTop: '20px' }}>{regionName}</Typography>
                            <List>
                                {groupedByRegion[regionName].map((country) => (
                                    <ListItemButton
                                        key={country.id}
                                        selected={country.code.toUpperCase() === countryCode}
                                        onClick={() => handleCountrySelect(country.name, country.code.toUpperCase(), country.id)}
                                        sx={{
                                            borderRadius: 1,
                                            color: 'text.primary',
                                            '&:hover': { bgcolor: 'action.hover' },
                                            '&.Mui-selected': { bgcolor: 'action.selected' },
                                            '&.Mui-selected:hover': { bgcolor: 'action.focus' },
                                        }}
                                    >
                                        <ListItemIcon>
                                            <Flag code={country.code} height="24" />
                                        </ListItemIcon>
                                        <ListItemText primary={country.name} />
                                    </ListItemButton>
                                ))}
                            </List>
                        </div>
                    ))}
                </Box>
            </Modal>
        </>
    );
};

export default RegionSelector;
